import React, { useState, useContext } from 'react';
import { WalletContext } from '../context/WalletContex';
import Button from '../components/ui/Button';
import { HiEye, HiEyeSlash, HiExclamationTriangle, HiCheckCircle, HiDocumentDuplicate } from 'react-icons/hi2';
import { toast } from 'react-toastify';

export default function MnemonicDisplay() {
  const { wallet, confirmMnemonicSeen } = useContext(WalletContext);
  const [revealed, setRevealed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const words = wallet?.mnemonic?.split(' ') ?? [];
  const [copied, setCopied] = useState(false);

  const copyPhrase = () => {
    navigator.clipboard.writeText(wallet?.mnemonic ?? '');
    setCopied(true);
    toast.success('Recovery phrase copied — store it somewhere safe!');
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      padding: '48px 24px', maxWidth: 430, margin: '0 auto',
      background: '#090817',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ width: 64, height: 64, borderRadius: 22, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <HiExclamationTriangle size={28} style={{ color: '#F59E0B' }} />
        </div>
        <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>
          Your Secret Recovery Phrase
        </h1>
        <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
          Write these 12 words down on paper in order and store them somewhere safe. They are the only way to recover your wallet if you lose access.
        </p>
      </div>

      {/* Warnings */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {[
          'Never share your phrase with anyone',
          'Ikhua will never ask you for it',
          'Anyone with these words controls your funds',
        ].map(w => (
          <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 10, padding: '9px 12px' }}>
            <span style={{ color: '#EF4444', fontSize: 13 }}>✕</span>
            <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: 500 }}>{w}</span>
          </div>
        ))}
      </div>

      {/* Word grid — blurred until revealed */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8,
          filter: revealed ? 'none' : 'blur(6px)',
          userSelect: revealed ? 'text' : 'none',
          transition: 'filter 0.2s ease',
        }}>
          {words.map((word, i) => (
            <div key={i} style={{
              background: '#1A1748', border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 10, padding: '8px 10px',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontSize: '10px', color: '#4B5170', fontWeight: 700, minWidth: 14 }}>{i + 1}</span>
              <span style={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>{word}</span>
            </div>
          ))}
        </div>

        {!revealed && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
            <button
              onClick={() => setRevealed(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#7C5CF6', border: 'none', borderRadius: 14,
                padding: '12px 20px', color: '#fff', fontSize: '14px',
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              <HiEye size={18} /> Reveal Phrase
            </button>
            <p style={{ color: '#4B5170', fontSize: '11px', margin: 0 }}>Make sure no one can see your screen</p>
          </div>
        )}
      </div>

      {/* Copy + Hide controls — only when revealed */}
      {revealed && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'center' }}>
          <button
            onClick={copyPhrase}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 20,
              background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(124,92,246,0.12)',
              border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'rgba(124,92,246,0.25)'}`,
              color: copied ? '#10B981' : '#7C5CF6',
              fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {copied
              ? <><HiCheckCircle size={14} /> Copied!</>
              : <><HiDocumentDuplicate size={14} /> Copy phrase</>
            }
          </button>
          <button
            onClick={() => setRevealed(false)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#4B5170', fontSize: '12px', cursor: 'pointer' }}
          >
            <HiEyeSlash size={14} /> Hide
          </button>
        </div>
      )}

      {/* Confirmation checkbox */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 24 }}>
        <div
          onClick={() => setConfirmed(c => !c)}
          style={{
            width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
            background: confirmed ? '#7C5CF6' : 'transparent',
            border: `2px solid ${confirmed ? '#7C5CF6' : 'rgba(255,255,255,0.2)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}
        >
          {confirmed && <HiCheckCircle size={14} style={{ color: '#fff' }} />}
        </div>
        <span style={{ fontSize: '13px', color: '#8B8FA8', lineHeight: 1.5 }}>
          I have written down my 12-word phrase and stored it safely. I understand that losing it means permanently losing access to my wallet.
        </span>
      </label>

      <Button
        variant="primary"
        size="lg"
        full
        disabled={!confirmed || !revealed}
        onClick={confirmMnemonicSeen}
      >
        I've Saved My Phrase — Continue
      </Button>

      <p style={{ textAlign: 'center', color: '#4B5170', fontSize: '11px', marginTop: 12 }}>
        You cannot go back to view this phrase. Store it now.
      </p>
    </div>
  );
}
